'use client';

import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Mail, Loader2 } from 'lucide-react';

export type LeadMagnetVariant =
  | 'moneyhub-migration'
  | 'fire-cgt-spreadsheet'
  | 'uk-stack-checklist'
  | 'clarity-score'
  | 'retirement-scorecard'
  | 'general';

interface LeadMagnetCTAProps {
  variant: LeadMagnetVariant;
  pageSlug: string;
  compact?: boolean;
}

const VARIANT_COPY: Record<
  LeadMagnetVariant,
  { headline: string; body: string; bullets: string[]; cta: string; promise: string }
> = {
  'moneyhub-migration': {
    headline: 'Get the Moneyhub-to-Delphina migration checklist (free)',
    body: 'Moneyhub has closed its consumer app. This 2-minute checklist tells you exactly what to export from your old account, where to import it, and what to check now that the deadline has passed.',
    bullets: [
      'How to export your Moneyhub transactions as CSV',
      'How to import them into Delphina (one click)',
      'The 3 things to verify before you delete your Moneyhub account',
      'What to do if you opted in to WPS Advisory instead',
    ],
    cta: 'Send me the checklist',
    promise: 'No spam. One email with the checklist and a single follow-up.',
  },
  'fire-cgt-spreadsheet': {
    headline: 'Get the FIRE + CGT planner spreadsheet (free)',
    body: 'A UK-specific Google Sheet. See your FI number, your projected CGT bill, and how they interact - all in one place.',
    bullets: [
      'UK state pension and 25% tax-free lump sum modelled',
      'ISA bridging and pension drawdown phases',
      'Bed & Breakfast rule CGT scenarios built in',
      'Drop in your numbers - see your answer in 10 minutes',
    ],
    cta: 'Send me the spreadsheet',
    promise: 'Free Google Sheet. One email with the link.',
  },
  'uk-stack-checklist': {
    headline: 'Get the UK Personal Finance Stack checklist (free)',
    body: 'The 7 tools every UK adult needs to track, plan, and protect their finances - and which to skip.',
    bullets: [
      'Current account, savings, and pension - what to use',
      'When you need an adviser vs when you don\'t',
      'How to spot and avoid the £200/hour trap',
      'A simple monthly rhythm that takes 20 minutes',
    ],
    cta: 'Send me the checklist',
    promise: 'One-page PDF. One email.',
  },
  'clarity-score': {
    headline: 'Get your 2-minute Financial Clarity Score',
    body: 'Answer 5 quick questions. Get a personal verdict on whether you\'re on track - no signup required.',
    bullets: [
      'Takes 2 minutes',
      'UK-specific (ISAs, state pension, pensions)',
      'Personal verdict - not a generic article',
      'No account, no commitment',
    ],
    cta: 'Get my Clarity Score',
    promise: 'Free. Instant. No signup.',
  },
  'retirement-scorecard': {
    headline: 'Is your retirement number enough?',
    body: 'A 5-minute scorecard that compares your projected pension against what you\'ll actually need - UK-specific.',
    bullets: [
      'Compare your number against UK averages',
      'See your shortfall (or surplus) at age 65',
      'UK state pension and 25% lump sum included',
      'Get a one-page report by email',
    ],
    cta: 'Get my scorecard',
    promise: '5 minutes. PDF by email.',
  },
  general: {
    headline: 'Get the free guide',
    body: 'A quick read that gives you the essentials in one page.',
    bullets: ['Free PDF', 'No spam', 'One email'],
    cta: 'Send me the guide',
    promise: 'One email. No follow-ups.',
  },
};

export default function LeadMagnetCTA({ variant, pageSlug, compact = false }: LeadMagnetCTAProps) {
  const copy = VARIANT_COPY[variant];
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          leadType: variant,
          data: { pageSlug, source: 'conversion-component' },
        }),
      });
      if (!res.ok) throw new Error('submit failed');
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="bg-white border-2 border-black shadow-brutal p-6 md:p-8 my-8">
      <div className="flex items-start gap-4 mb-4">
        <div className="bg-warning-600 text-white w-12 h-12 flex items-center justify-center border-2 border-ink flex-shrink-0">
          <Mail className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-xl md:text-2xl font-bold mb-2 text-gray-900">{copy.headline}</h3>
          <p className="text-gray-700">{copy.body}</p>
        </div>
      </div>

      {!compact && (
        <ul className="space-y-2 mb-6 pl-1">
          {copy.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-gray-700">
              <CheckCircle className="w-5 h-5 text-positive-600 flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {status === 'success' ? (
        <div className="bg-positive-50 border-2 border-positive-500 p-4 flex items-start gap-3">
          <CheckCircle className="w-6 h-6 text-positive-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-gray-900 mb-1">Check your inbox.</p>
            <p className="text-gray-700 text-sm">
              We've sent {variant === 'clarity-score' ? 'a link to your score' : 'the ' + (variant === 'moneyhub-migration' ? 'checklist' : variant === 'fire-cgt-spreadsheet' ? 'spreadsheet link' : 'PDF')} to <strong>{email}</strong>.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
              placeholder="you@example.com"
              aria-label="Your email address"
              className="flex-1 px-4 py-3 border-2 border-black bg-white focus:outline-none focus:ring-2 focus:ring-positive-500 text-gray-900"
              disabled={status === 'submitting'}
            />
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex items-center justify-center gap-2 bg-positive-600 hover:bg-positive-700 text-white font-bold py-3 px-6 rounded-md border-2 border-ink shadow-brutal-sm hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {status === 'submitting' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
              ) : (
                <>{copy.cta} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
          {status === 'error' && (
            <p className="text-danger-600 text-sm">{errorMsg}</p>
          )}
          <p className="text-xs text-gray-500">{copy.promise}</p>
        </form>
      )}
    </div>
  );
}